import React, { useState } from 'react';
import { Card, Button } from './shared/SharedComponents';
import { Mail, ArrowRight, CheckCircle2 } from 'lucide-react';
import { auditService } from '../services/auditService';
import { emailService } from '../services/emailService';
import { AuditResult } from '../types';

interface LeadCaptureProps {
  auditId: string;
  result: AuditResult;
}

export const LeadCapture: React.FC<LeadCaptureProps> = ({ auditId, result }) => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [honeypot, setHoneypot] = useState('');

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isSandboxRestricted, setIsSandboxRestricted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // Silent fail for bots
    
    setIsSubmitting(true);
    setErrorMessage(null);
    setIsSandboxRestricted(false);
    
    try {
      await auditService.captureLead(auditId, email, company);
      const res = await emailService.sendAuditReport(email, result);
      
      if (res && res.html) {
        setPreviewHtml(res.html);
      }
      
      if (res && !res.success) {
        if (res.isSandboxRestriction) {
          setIsSandboxRestricted(true);
          setIsSubmitted(true); // Show success/preview view
        } else {
          setErrorMessage(res.message || 'Failed to dispatch email.');
        }
      } else {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to capture lead:', error);
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="space-y-6">
        <Card className="bg-primary/5 border-primary/20 p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto text-primary">
            <CheckCircle2 size={32} />
          </div>
          
          {isSandboxRestricted ? (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="space-y-2 text-center">
                <h3 className="text-3xl font-serif italic text-foreground tracking-tight">Interactive Preview Generated</h3>
                <p className="text-muted-foreground text-sm">
                  Your personalized SaaS audit report is compiled and ready for review.
                </p>
              </div>

              {/* Developer Glassmorphic Notice */}
              <div className="text-left relative overflow-hidden rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6 backdrop-blur-md shadow-[0_8px_32px_rgba(245,158,11,0.05)] transition-all duration-300 hover:border-amber-500/30">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none" />
                
                <div className="flex gap-4 items-start relative z-10">
                  <div className="p-3 bg-amber-500/10 rounded-xl text-amber-500 shrink-0 font-mono text-2xl select-none">
                    👋
                  </div>
                  <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="text-base font-semibold text-foreground font-sans">
                        Hi, I'm Tarunya!
                      </h4>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-amber-500/10 text-amber-600 border border-amber-500/20">
                        Sandbox Restriction Notice
                      </span>
                    </div>
                    
                    <p className="text-sm text-foreground/85 leading-relaxed">
                      Due to cost constraints, this application is running in <span className="font-semibold text-amber-600">Resend's free-tier sandbox mode</span>. This mode restricts actual inbox delivery to verified recipient addresses.
                    </p>

                    <div className="border-t border-amber-500/10 pt-3 mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block font-bold">1. Immediate Access</span>
                        <p className="text-xs text-foreground/70 leading-relaxed">
                          Your full report has been compiled and is ready for inspection. Toggle the preview below to view it.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block font-bold">2. Test Live Delivery</span>
                        <p className="text-xs text-foreground/70 leading-relaxed">
                          Want real-time email notifications on pricing updates? Shoot me an email to register your address:
                          <a 
                            href="mailto:tarunya.programmer@gmail.com" 
                            className="block mt-1 font-mono font-bold text-primary hover:underline text-xs"
                          >
                            tarunya.programmer@gmail.com
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <h3 className="text-2xl font-serif italic text-foreground">Report Dispatched</h3>
              <p className="text-muted-foreground max-w-md mx-auto text-sm">
                Check your inbox. The full executive brief and implementation roadmap are on their way.
              </p>
            </div>
          )}

          {previewHtml && (
            <div className="pt-2 flex justify-center">
              <Button 
                onClick={() => setShowPreview(!showPreview)} 
                variant="outline" 
                size="sm"
                className="font-mono text-xs"
              >
                {showPreview ? 'Hide Email Preview' : 'Preview Dispatched Email'}
              </Button>
            </div>
          )}
        </Card>

        {showPreview && previewHtml && (
          <Card padding="none" className="border border-foreground/10 overflow-hidden shadow-2xl transition-all duration-500 bg-zinc-900">
            <div className="bg-zinc-800 px-4 py-2 border-b border-foreground/10 flex items-center gap-2 text-xs font-mono text-zinc-400">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              <span className="ml-2 font-semibold">report-email-preview.html</span>
            </div>
            <div className="p-4 bg-white">
              <iframe 
                srcDoc={previewHtml} 
                title="Email Report Preview" 
                className="w-full h-[550px] border-0 rounded-md"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <Card padding="lg" className="bg-foreground text-background overflow-hidden relative">
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <h3 className="text-4xl font-serif italic tracking-tight">Preserve these insights.</h3>
          <p className="text-background/60 text-lg">
            Email this audit report to yourself or your finance team. We'll include a high-resolution PDF and a strategic implementation guide.
          </p>
          <div className="flex gap-4 items-center text-xs text-background/40 font-mono uppercase tracking-widest">
            <span className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-primary rounded-full" /> No Spam</span>
            <span className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-primary rounded-full" /> PDF Included</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Lead capture form">
          {errorMessage && (
            <div className="p-3 bg-red-950/60 border border-red-500/30 text-red-200 rounded-xl text-xs font-mono">
              Error: {errorMessage}
            </div>
          )}
          <input 
            id="hp-field"
            type="text" 
            name="b_phone" 
            style={{ display: 'none' }} 
            tabIndex={-1} 
            aria-hidden="true"
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
          <div className="space-y-2">
            <label htmlFor="lead-email" className="text-[10px] uppercase tracking-widest opacity-40 font-bold">Work Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-background/20" size={18} aria-hidden="true" />
              <input 
                id="lead-email"
                type="email" 
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="founder@company.com"
                className="w-full bg-background/5 border border-background/10 rounded-xl p-4 pl-12 text-background outline-none focus:border-primary/50 transition-all"
                aria-required="true"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="lead-company" className="text-[10px] uppercase tracking-widest opacity-40 font-bold">Company (Optional)</label>
            <input 
              id="lead-company"
              type="text" 
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Corp"
              className="w-full bg-background/5 border border-background/10 rounded-xl p-4 text-background outline-none focus:border-primary/50 transition-all"
            />
          </div>
          <Button 
            type="submit" 
            size="xl" 
            className="w-full group"
            disabled={isSubmitting}
            aria-label={isSubmitting ? 'Dispatching audit report' : 'Send My Audit Report'}
          >
            {isSubmitting ? 'Dispatching...' : 'Send My Audit Report'} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </Button>

          <p className="text-[10px] text-background/50 font-mono leading-relaxed mt-4 border-t border-background/10 pt-4">
            * Developer Notice: Running in Resend Sandbox Mode. To test delivery to your actual inbox, enter 
            <strong className="text-primary font-bold"> tarunya.programmer@gmail.com</strong>. Entering any other email 
            will generate a live interactive preview of the dispatched email below.
          </p>
        </form>
      </div>
      <div className="absolute -left-24 -top-24 w-64 h-64 bg-primary/20 rounded-full blur-[80px]" />
    </Card>
  );
};
